import { Dispatch, SetStateAction, memo } from 'react';
import { Alert, Box, Center, Loader, Table as MantineTable, Text } from '@mantine/core';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { formatMoneyString } from '../../../utils/strings';
import Table from '../../../components/Table';
import { useGetStaffActivityDetails } from '../../../hooks/reports/queries';
import { StaffTableInfoData } from '.';

interface DrillDownPanelProps {
  storeId: string;
  employeeId: string;
  fromDate?: string;
  toDate?: string;
}

function DrillDownPanel({ storeId, employeeId, fromDate, toDate }: DrillDownPanelProps) {
  const { t } = useTranslation(['ReportsPage', 'FieldName']);
  const { data, isLoading, isError } = useGetStaffActivityDetails(storeId, employeeId, {
    fromDate,
    toDate,
  });

  if (isLoading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert color="red" py="xs">
        {t('ReportsPage:staffReport.drillDown.error')}
      </Alert>
    );
  }

  const rows: any[] = data?.details ?? [];

  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="xs" px="md">
        {t('ReportsPage:staffReport.drillDown.noData')}
      </Text>
    );
  }

  return (
    <Box px="md" py="xs">
      <MantineTable striped withColumnBorders withTableBorder fz="sm">
        <MantineTable.Thead>
          <MantineTable.Tr>
            <MantineTable.Th>{t('ReportsPage:staffReport.drillDown.columns.dateTime')}</MantineTable.Th>
            <MantineTable.Th>{t('ReportsPage:staffReport.drillDown.columns.action')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:folio')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:customer')}</MantineTable.Th>
            <MantineTable.Th>{t('ReportsPage:staffReport.drillDown.columns.amount')}</MantineTable.Th>
            <MantineTable.Th>{t('ReportsPage:staffReport.drillDown.columns.paymentMethod')}</MantineTable.Th>
          </MantineTable.Tr>
        </MantineTable.Thead>
        <MantineTable.Tbody>
          {rows.map((row: any, idx: number) => (
            <MantineTable.Tr key={`${row.orderId}-${row.actionKey}-${idx}`}>
              <MantineTable.Td>
                {row.actionDate ? moment(row.actionDate).format('DD/MM/YY HH:mm') : '—'}
              </MantineTable.Td>
              <MantineTable.Td>
                {t(`FieldName:activityAction.${row.actionKey}`, { defaultValue: row.actionKey })}
              </MantineTable.Td>
              <MantineTable.Td>{row.orderFolio || '—'}</MantineTable.Td>
              <MantineTable.Td>{row.customerName || '—'}</MantineTable.Td>
              <MantineTable.Td>
                {row.amount != null ? formatMoneyString(Number(row.amount).toFixed(2)) : '—'}
              </MantineTable.Td>
              <MantineTable.Td>{row.paymentMethod || '—'}</MantineTable.Td>
            </MantineTable.Tr>
          ))}
        </MantineTable.Tbody>
      </MantineTable>
    </Box>
  );
}

export default memo(function TableView({
  storeId,
  fromDate,
  toDate,
  tableInfoData = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination
}: {
  storeId: string;
  fromDate?: string;
  toDate?: string;
  tableInfoData: StaffTableInfoData[];
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t } = useTranslation(['FieldName']);
  const columns = [
    {
      accessorFn: (row) => row.user?.name,
      header: t('FieldName:name')
    },
    {
      accessorFn: (row) => row.ordersCreated,
      header: t('FieldName:ordersCreated')
    },
    {
      accessorFn: (row) => row.ordersCleaned,
      header: t('FieldName:ordersCleaned')
    },
    {
      accessorFn: (row) => row.ordersCollected,
      header: t('FieldName:ordersCollected')
    },
    {
      accessorFn: (row) => row.ordersCancelled,
      header: t('FieldName:ordersCancelled')
    },
    {
      accessorFn: (row) => row.paymentsRegistered || 0,
      header: t('FieldName:paymentsRegistered')
    },
    {
      accessorFn: (row) => formatMoneyString(Number(row.paymentsRegisteredAmount || 0).toFixed(2)),
      header: t('FieldName:paymentsRegisteredAmount')
    }
  ];

  return (
    <>
      <Table
        tableColumns={columns}
        pagination={pagination}
        setPagination={setPagination}
        tableData={tableInfoData}
        isError={isError}
        paginationIsLoading={isLoading}
        numPages={pages}
        totalRowsCount={totalRowsCount}
        overrideTableConfigs={{
          manualPagination: false,
          renderDetailPanel: ({ row }: { row: { original: StaffTableInfoData } }) => {
            const employeeId = String(row.original._id);
            if (!/^[0-9a-f]{24}$/i.test(employeeId)) return null;
            return (
              <DrillDownPanel
                storeId={storeId}
                employeeId={employeeId}
                fromDate={fromDate}
                toDate={toDate}
              />
            );
          }
        }}
      />
    </>
  );
});
